/**
 * Open (or refocus) a Dependency Viewer tab in find-usages mode for a column.
 *
 * This is the shared destination for every "Find usages…" entry point — the
 * Object Explorer column menu, BOTH results-grid header menus (the Query Editor
 * grid and the Table Explorer grid are separate components), and the Schema
 * Overview drawer's column rows. Routing them all through one function is what
 * keeps those surfaces honestly identical: same tab shape, same title, same
 * analytics, no per-call-site drift.
 *
 * The tab is "about" the column's owning table, so the Tree and Graph modes
 * still answer their own question if the user switches away from the search.
 *
 * Analytics is fired here rather than at the call sites (this is not a React
 * component) and carries ONLY the engine and a categorical source — never the
 * column, table, database or connection name.
 */
import { useWorkspaceStore } from '../../core/store/workspaceStore';
import { getEngineForConnection } from '../../../connections/utils/connectionLookup';
import type { AnalyticsEventData } from '../../../../core/analytics/analyticsTypes';

/** Where the user invoked "Find usages…" from. Categorical, no PII. */
export type FindUsagesSource =
  | 'object_explorer_column'
  | 'query_grid_header'
  | 'table_explorer_grid_header'
  | 'schema_overview_drawer'
  | 'dependency_viewer'
  | 'zero_match_notice';

export type OpenFindUsagesTabContext = {
  connectionId: string;
  database: string;
  schema: string;
  /** Table that owns the column — the object the tab is "about". */
  table: string;
  /** The text to search for inside SQL definitions (normally a column name). */
  columnName: string;
  source: FindUsagesSource;
};

export function trackFindUsages(connectionId: string | undefined, source: FindUsagesSource): void {
  if (typeof window === 'undefined') return;
  if (!window.electronAPI?.analytics?.track) return;
  const payload: AnalyticsEventData = {
    engine: connectionId ? getEngineForConnection(connectionId) ?? 'unknown' : 'unknown',
    source,
  };
  window.electronAPI.analytics.track('feature:find_usages', payload).catch(() => {
    // Analytics must never affect UX.
  });
}

/**
 * Fires once per completed catalog column-reference lookup (B8).
 *
 * Separate from `feature:find_usages` because the two legs answer different
 * questions and can succeed independently; knowing how often the precise leg
 * actually produces facts is the whole point of instrumenting it. Categorical
 * only — engine plus a bucketed outcome, never a column, table, database or
 * connection name, and never a count.
 */
export function trackCatalogReferences(
  connectionId: string | undefined,
  result: {
    error?: string;
    outcome?: {
      columnFound: boolean;
      targetFound: boolean;
      columnItems: unknown[];
      expandedItems: unknown[];
    };
  }
): void {
  if (typeof window === 'undefined') return;
  if (!window.electronAPI?.analytics?.track) return;
  // `target_not_found` must be its own bucket. Keying on columnFound alone
  // folded "we could not resolve the object" into "the user searched for
  // something that is not a column" — burying the one number that would show
  // how often the app cannot resolve an object inside the very claim that state
  // exists to disprove.
  const outcome = result.error
    ? 'error'
    : !result.outcome
      ? 'error'
      : !result.outcome.targetFound
        ? 'target_not_found'
        : !result.outcome.columnFound
          ? 'not_a_column'
          : result.outcome.columnItems.length + result.outcome.expandedItems.length > 0
            ? 'hit'
            : 'empty';
  const payload: AnalyticsEventData = {
    engine: connectionId ? getEngineForConnection(connectionId) ?? 'unknown' : 'unknown',
    outcome,
  };
  window.electronAPI.analytics.track('feature:find_usages_catalog', payload).catch(() => {
    // Analytics must never affect UX.
  });
}

export function openFindUsagesTab(context: OpenFindUsagesTabContext): void {
  const searchTerm = context.columnName.trim();
  if (!searchTerm) return;

  trackFindUsages(context.connectionId, context.source);

  const { tabs, addTab, setActiveTab, updateTabContent } = useWorkspaceStore.getState();

  const existing = tabs.find(
    (t) =>
      t.type === 'dependency-viewer' &&
      t.connectionInfo?.connectionId === context.connectionId &&
      t.content.database === context.database &&
      t.content.schema === context.schema &&
      t.content.objectName === context.table
  );

  if (existing) {
    updateTabContent<'dependency-viewer'>(existing.id, {
      viewMode: 'find-usages',
      // Replace wholesale: keeping a previous outcome here would let the old
      // column's hits be read as this column's answer.
      findUsages: { searchTerm },
    });
    setActiveTab(existing.id);
    return;
  }

  addTab({
    type: 'dependency-viewer',
    title: `Find usages: ${searchTerm}`,
    connectionInfo: {
      connectionId: context.connectionId,
      database: context.database,
    },
    content: {
      objectName: context.table,
      objectType: 'table',
      schema: context.schema,
      database: context.database,
      viewMode: 'find-usages',
      findUsages: { searchTerm },
      isLoading: true,
    },
  });
}
